import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Bell,
  Inbox,
  Settings,
  Send,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Mail,
  FileText,
  ArrowRight,
  RefreshCw
} from 'lucide-react';

const NotificacionesHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    unread: 0,
    total: 0,
    todaySent: 0
  });
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [countRes, inboxRes] = await Promise.all([
        api.get('/notifications/v2/inbox/count'),
        api.get('/notifications/v2/inbox?limit=5')
      ]);
      
      setStats(prev => ({
        ...prev,
        unread: countRes.data?.unread_count || 0
      }));
      setRecentNotifications(inboxRes.data || []);
    } catch (error) {
      console.error('Error loading notifications data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = user?.rol?.toUpperCase() === 'ADMIN' || 
                  user?.rol?.toUpperCase() === 'SUPERADMIN' || 
                  user?.rol?.toUpperCase() === 'ADMINISTRADOR';

  const menuItems = [
    {
      title: 'Mi Bandeja',
      description: 'Ver mis notificaciones',
      icon: Inbox,
      path: '/m/notificaciones/inbox',
      color: 'bg-indigo-500',
      badge: stats.unread > 0 ? stats.unread : null
    },
    ...(isAdmin ? [{
      title: 'Configuración',
      description: 'Administrar notificaciones',
      icon: Settings,
      path: '/m/notificaciones/admin',
      color: 'bg-slate-600'
    }] : []),
    ...(isAdmin ? [{
      title: 'Historial de Envíos',
      description: 'Ver log de notificaciones',
      icon: Clock,
      path: '/m/notificaciones/admin',
      color: 'bg-purple-500',
      tab: 'log'
    }] : [])
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Bell className="text-indigo-600" size={28} />
            </div>
            Notificaciones
          </h1>
          <p className="text-slate-500 mt-2">
            Gestiona tus alertas y configuración de notificaciones
          </p>
        </div>
        {stats.unread > 0 && (
          <Badge className="bg-red-500 text-white text-lg px-4 py-2">
            {stats.unread} sin leer
          </Badge>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-white border-indigo-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">No Leídas</p>
                <p className="text-3xl font-bold text-indigo-600">{stats.unread}</p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Bell size={24} className="text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-green-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Email Activo</p>
                <p className="text-xl font-bold text-green-600 flex items-center gap-2">
                  <CheckCircle size={20} />
                  Configurado
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Mail size={24} className="text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-purple-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Digest Diario</p>
                <p className="text-xl font-bold text-purple-600 flex items-center gap-2">
                  <FileText size={20} />
                  8:00 AM
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Clock size={24} className="text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Menu Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {menuItems.map((item, idx) => {
          const Icon = item.icon;
          return (
            <Card
              key={idx}
              className="bg-white hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => navigate(item.path)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className={`p-3 ${item.color} rounded-lg`}>
                    <Icon size={24} className="text-white" />
                  </div>
                  {item.badge && (
                    <Badge className="bg-red-500 text-white">{item.badge}</Badge>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mt-4 group-hover:text-indigo-600 transition-colors">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                <div className="flex items-center text-indigo-600 mt-3 text-sm font-medium">
                  Ir <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Notifications */}
      <Card className="bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Clock size={18} className="text-slate-400" />
              Notificaciones Recientes
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/m/notificaciones/inbox')}
            >
              Ver todas <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-slate-400" />
            </div>
          ) : recentNotifications.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Bell size={32} className="mx-auto mb-2 opacity-50" />
              <p>No hay notificaciones recientes</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentNotifications.map((n, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${!n.read ? 'bg-blue-50/50 border-blue-100' : 'bg-slate-50 border-slate-100'} hover:bg-slate-100 cursor-pointer transition-colors`}
                  onClick={() => navigate('/m/notificaciones/inbox')}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${!n.read ? 'text-slate-900' : 'text-slate-700'}`}>
                          {n.title}
                        </span>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500"></span>}
                      </div>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-1">{n.message}</p>
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap ml-4">
                      {formatDate(n.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificacionesHome;
